import { useRef, useEffect, useCallback, useState, type ReactNode, type TouchEvent } from 'react';
import { createPortal } from 'react-dom';
import { useBodyOverflowLock } from '@/hooks/useBodyOverflowLock';

interface BottomSheetProps {
  /** Whether the sheet is open */
  isOpen: boolean;
  /** Callback when sheet should close */
  onClose: () => void;
  /** Initial height as percentage of viewport (default: 50) */
  initialHeight?: number;
  /** Minimum height as percentage of viewport (default: 20) */
  minHeight?: number;
  /** Maximum height as percentage of viewport (default: 90) */
  maxHeight?: number;
  /** Content to render inside the sheet */
  children: ReactNode;
  /** Optional title for the sheet header */
  title?: string;
}

/**
 * Reusable draggable bottom sheet component.
 * Supports drag-to-resize and drag-to-close gestures.
 * Optimized for iOS Safari and mobile touch interactions.
 */
export function BottomSheet({
  isOpen,
  onClose,
  initialHeight = 50,
  minHeight = 20,
  maxHeight = 90,
  children,
  title,
}: BottomSheetProps) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const dragHandleRef = useRef<HTMLDivElement>(null);

  // Current height as percentage of viewport
  const [currentHeight, setCurrentHeight] = useState(initialHeight);
  // Whether the sheet is currently being dragged
  const [isDragging, setIsDragging] = useState(false);
  // Whether the sheet is animating (entry/exit)
  const [isAnimating, setIsAnimating] = useState(false);
  // Whether the sheet is visible (for CSS transitions)
  const [isVisible, setIsVisible] = useState(false);

  // Touch tracking refs
  const startYRef = useRef(0);
  const startHeightRef = useRef(0);
  const velocityRef = useRef(0);
  const lastYRef = useRef(0);
  const lastTimeRef = useRef(0);

  // Close threshold: if dragged below this percentage, close the sheet
  const CLOSE_THRESHOLD = 15;
  // Velocity threshold: if swiping down faster than this, close the sheet
  const VELOCITY_THRESHOLD = 0.5;

  // Handle opening animation
  useEffect(() => {
    if (isOpen) {
      setCurrentHeight(initialHeight);
      setIsAnimating(true);
      // Small delay to trigger CSS transition
      const finishOpeningAnimation = () => {
        setIsVisible(true);
        setTimeout(() => setIsAnimating(false), 300);
      };
      requestAnimationFrame(() => {
        requestAnimationFrame(finishOpeningAnimation);
      });
    } else {
      setIsVisible(false);
      setTimeout(() => {
        setIsAnimating(false);
      }, 300);
    }
    // Note: initialHeight is intentionally excluded - handled in separate effect below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Update height when initialHeight changes (e.g., switching views within the sheet)
  useEffect(() => {
    if (isOpen && isVisible && !isDragging) {
      setCurrentHeight(initialHeight);
    }
  }, [initialHeight, isOpen, isVisible, isDragging]);

  // Lock body overflow when sheet is open
  useBodyOverflowLock(isOpen);

  // Add class for Safari workaround when sheet is open
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('bottom-sheet-open');
      return () => {
        document.body.classList.remove('bottom-sheet-open');
      };
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleTouchStart = useCallback(
    (e: TouchEvent) => {
      const touch = e.touches[0];
      startYRef.current = touch.clientY;
      startHeightRef.current = currentHeight;
      lastYRef.current = touch.clientY;
      lastTimeRef.current = Date.now();
      velocityRef.current = 0;
      setIsDragging(true);
    },
    [currentHeight]
  );

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!isDragging) return;

      const touch = e.touches[0];
      const currentY = touch.clientY;
      const deltaY = startYRef.current - currentY;
      const viewportHeight = window.innerHeight;

      // Calculate velocity (pixels per millisecond)
      const now = Date.now();
      const timeDelta = now - lastTimeRef.current;
      if (timeDelta > 0) {
        velocityRef.current = (currentY - lastYRef.current) / timeDelta;
      }
      lastYRef.current = currentY;
      lastTimeRef.current = now;

      // Convert delta to percentage of viewport
      const deltaPercent = (deltaY / viewportHeight) * 100;
      let newHeight = startHeightRef.current + deltaPercent;

      // Clamp to min/max with rubber band effect at edges
      if (newHeight > maxHeight) {
        const overshoot = newHeight - maxHeight;
        newHeight = maxHeight + overshoot * 0.2;
      } else if (newHeight < minHeight) {
        const undershoot = minHeight - newHeight;
        newHeight = minHeight - undershoot * 0.3;
      }

      setCurrentHeight(newHeight);
    },
    [isDragging, maxHeight, minHeight]
  );

  const handleTouchEnd = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);

    // Check if should close based on position or velocity
    const shouldClose = currentHeight < CLOSE_THRESHOLD || velocityRef.current > VELOCITY_THRESHOLD;

    if (shouldClose) {
      onClose();
    } else {
      // Snap to valid range
      if (currentHeight > maxHeight) {
        setCurrentHeight(maxHeight);
      } else if (currentHeight < minHeight) {
        setCurrentHeight(minHeight);
      }
    }
  }, [isDragging, currentHeight, maxHeight, minHeight, onClose]);

  // Handle backdrop click
  const handleBackdropClick = useCallback(() => {
    onClose();
  }, [onClose]);

  if (!isOpen && !isAnimating) return null;

  // Use portal to render at document root for proper z-index stacking on Safari
  const content = (
    <>
      {/* Backdrop - Safari-compatible with full screen coverage */}
      <div
        className={`
          fixed z-[9998] bg-black/50
          transition-opacity duration-300
          ${isVisible ? 'opacity-100' : 'opacity-0'}
        `}
        style={{
          // Cover entire screen including safe areas
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          // Extend beyond safe areas
          marginTop: 'calc(-1 * env(safe-area-inset-top, 0px))',
          marginBottom: 'calc(-1 * env(safe-area-inset-bottom, 0px))',
          paddingTop: 'env(safe-area-inset-top, 0px)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          // Safari requires -webkit prefix for backdrop-filter
          WebkitBackdropFilter: 'blur(4px)',
          backdropFilter: 'blur(4px)',
          // Force GPU layer for Safari
          transform: 'translateZ(0)',
          WebkitTransform: 'translateZ(0)',
        }}
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      {/* Sheet */}
      <div
        ref={sheetRef}
        className={`
          fixed left-0 right-0 bottom-0 z-[9999]
          bg-white rounded-t-2xl shadow-2xl
          flex flex-col
          ${isDragging ? '' : 'transition-all duration-300 ease-out'}
        `}
        style={{
          height: isVisible ? `calc(var(--app-height, 100vh) * ${currentHeight / 100})` : '0',
          maxHeight: `calc(var(--app-height, 100vh) * ${maxHeight / 100})`,
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          transform: isVisible ? 'translateY(0)' : 'translateY(100%)',
        }}
      >
        {/* Drag handle area */}
        <div
          ref={dragHandleRef}
          className="w-full pt-3 pb-2 cursor-grab active:cursor-grabbing touch-none shrink-0"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchEnd}
        >
          {/* Visual drag indicator */}
          <div className="w-10 h-1 mx-auto bg-slate-300 rounded-full" />

          {/* Optional title */}
          {title && (
            <div className="mt-3 px-4 text-center">
              <span className="font-medium text-slate-700">{title}</span>
            </div>
          )}
        </div>

        {/* Content area - flex-1 to fill remaining space */}
        <div className="flex-1 overflow-y-auto overscroll-contain min-h-0">{children}</div>
      </div>
    </>
  );

  // Portal to document.body for proper stacking context on Safari
  return createPortal(content, document.body);
}
